import axios, { InternalAxiosRequestConfig } from "axios";
import { getOrCreateVisitorId } from "./visitorId";
import { useAuthStore } from "../store/authStore";

const rootApiUrl = import.meta.env.DEV
  ? "http://localhost:3001/v1"
  : import.meta.env.VITE_IS_STAGING === "true"
    ? "https://staging-backend.ghibgen.com/v1"
    : "https://backend.ghibgen.com/v1";

export const apiUrls = {
  generateArt: `${rootApiUrl}/art-generator/upload-files`,
  regenerateArt: (id: string) => `${rootApiUrl}/art-generator/regenerate/${id}`,
  getArtStatus: (id: string) => `${rootApiUrl}/art-generator/art-status/${id}`,
  getArtMeta: (id: string) => `${rootApiUrl}/art-generator/art/${id}`,
  listMyArts: `${rootApiUrl}/art-generator/my-arts`,
  addEmail: (id: string) => `${rootApiUrl}/art-generator/update-email/${id}`,
  shippingEstimate: `${rootApiUrl}/payments/shipping-estimate`,
  createCheckoutSession: `${rootApiUrl}/payments/create-checkout-session`,
  checkPayment: (sessionId: string) =>
    `${rootApiUrl}/payments/check-payment/${sessionId}`,
  updateUser: (id: string) => `${rootApiUrl}/users/${id}`,
  getUser: `${rootApiUrl}/users/me`,
  auth: {
    login: `${rootApiUrl}/auth/login`,
    forgotPassword: `${rootApiUrl}/auth/forgot-password`,
    resetPassword: `${rootApiUrl}/auth/reset-password`,
    register: `${rootApiUrl}/auth/register`,
    googleVerifyUser: `${rootApiUrl}/auth/google/verify/user`,
    refreshTokens: `${rootApiUrl}/auth/refresh-tokens`,
    logout: `${rootApiUrl}/auth/logout`,
  },
  wall: {
    list: `${rootApiUrl}/wall`,
    feed: `${rootApiUrl}/wall/feed`,
    get: (id: string) => `${rootApiUrl}/wall/${id}`,
    like: `${rootApiUrl}/wall/like`,
    unlike: `${rootApiUrl}/wall/unlike`,
  },
  shop: {
    posterTemplates: `${rootApiUrl}/shop/poster-templates`,
    posterTemplate: (slug: string) =>
      `${rootApiUrl}/shop/poster-templates/${slug}`,
    createPosterTemplatePreviewSession: (slug: string) =>
      `${rootApiUrl}/shop/poster-templates/${slug}/preview-session`,
    personalizationSession: (sessionId: string) =>
      `${rootApiUrl}/shop/poster-templates/personalization-sessions/${sessionId}`,
    updatePersonalizationSessionSource: (sessionId: string) =>
      `${rootApiUrl}/shop/poster-templates/personalization-sessions/${sessionId}/source-image`,
    generatePersonalizationSession: (sessionId: string) =>
      `${rootApiUrl}/shop/poster-templates/personalization-sessions/${sessionId}/generate`,
    finalizePersonalizationSession: (sessionId: string) =>
      `${rootApiUrl}/shop/poster-templates/personalization-sessions/${sessionId}/finalize`,
    uploadPosterTemplateSource: (slug: string) =>
      `${rootApiUrl}/shop/poster-templates/${slug}/upload`,
  },
  merch: {
    products: (artId: string, index: number) =>
      `${rootApiUrl}/merch/products?artId=${artId}&index=${index}`,
    stickerProducts: (artId: string, index: number) =>
      `${rootApiUrl}/merch/products/stickers?artId=${artId}&index=${index}`,
    blanketProducts: (artId: string, index: number) =>
      `${rootApiUrl}/merch/products/blankets?artId=${artId}&index=${index}`,
  },
  unsubscribeMemories: (args: { token: string; redirect?: string }) => {
    const redirect = args.redirect
      ? `&redirect=${encodeURIComponent(args.redirect)}`
      : "";
    return `${rootApiUrl}/unsubscribe/memories?token=${encodeURIComponent(args.token)}${redirect}`;
  },
};

export const api = axios.create({
  baseURL: rootApiUrl,
  headers: { "Content-Type": "application/json" },
});

/* ------------------------------------------------------------------ */
/*  Token refresh machinery                                            */
/* ------------------------------------------------------------------ */

const refreshClient = axios.create({ baseURL: rootApiUrl });

function isExpiringSoon(expires: string): boolean {
  return new Date(expires).getTime() - Date.now() < 60_000;
}

let refreshingPromise: Promise<void> | null = null;

async function ensureAccessToken(): Promise<string | undefined> {
  const { tokens, setTokens } = useAuthStore.getState();
  if (!tokens?.access?.token) return undefined;

  if (!isExpiringSoon(tokens.access.expires)) {
    return tokens.access.token;
  }

  const refreshToken = tokens.refresh?.token;
  if (!refreshToken) return undefined;

  if (!refreshingPromise) {
    refreshingPromise = (async () => {
      try {
        const res = await refreshClient.post("/auth/refresh-tokens", {
          refreshToken,
        });
        setTokens({ access: res.data.access, refresh: res.data.refresh });
      } catch {
        setTokens(null);
      } finally {
        refreshingPromise = null;
      }
    })();
  }

  await refreshingPromise;
  return useAuthStore.getState().tokens?.access?.token;
}

/* ------------------------------------------------------------------ */
/*  Request interceptor: visitor-id + auth Bearer                      */
/* ------------------------------------------------------------------ */

interface SkipAuthConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
}

api.interceptors.request.use(async (config) => {
  if (typeof window !== "undefined") {
    const visitorId = getOrCreateVisitorId();
    if (visitorId) {
      config.headers["X-Ghibgen-Visitor-Id"] = visitorId;
    }
  }

  if ((config as SkipAuthConfig).skipAuth) return config;

  try {
    const token = await ensureAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // proceed unauthenticated
  }

  return config;
});
